export const site = {
  brand: "知汐 Labs",
  logo: "/assets/zhixi-logo.png",
  email: "hello@zhixi-labs.com",
  personalDiagnosisUrl: "https://risk.zhixilabs.com/",
  icp: "粤ICP备2026071881号-1",
  icpUrl: "https://beian.miit.gov.cn",
  policeBeian: "粤公网安备44010502004160号",
  policeBeianUrl: "https://beian.mps.gov.cn/#/query/webSearch?code=44010502004160",
  policeBeianIcon: "/assets/beian-police.png"
} as const;

export const navItems = [
  { label: "岗位升值初筛", href: "#career-upgrade" },
  { label: "样例报告", href: "#sample-report" },
  { label: "岗位观察", href: "#insights" },
  { label: "关于知汐", href: "#about" },
  { label: "联系我们", href: "#contact" }
] as const;

export const hero = {
  eyebrow: "AI 岗位升值初筛",
  titleStart: "看清你的岗位任务，先",
  titleHighlight: "升级",
  titleEnd: "最该升级的一步",
  text:
    "识别哪些任务正在被 AI 贬值，哪些能力正在升值，并获得你能立刻上手的第一个 AI 工作流。",
  placeholder: "你的岗位是？例如：内容运营 / 产品经理 / HR",
  inputButton: "开始分析",
  primary: "免费生成岗位初筛",
  secondary: "查看样例报告",
  badges: ["免费初筛", "2 分钟生成", "基于岗位真实任务", "隐私安全保障"]
} as const;

export const scannerDemo = {
  title: "AI 岗位任务扫描",
  progress: "分析中…78%",
  metrics: [
    { label: "任务识别", value: "32 项" },
    { label: "风险评估", value: "中风险" },
    { label: "升值能力", value: "6 项" },
    { label: "建议动作", value: "1 个" }
  ],
  riskTitle: "风险评估结果",
  verdict: "中风险",
  verdictText: "部分标准化环节正在被 AI 改变。",
  gaugeLabels: ["低风险", "中风险", "高风险"],
  workflowTitle: "你的第一个 AI 上手动作",
  workflow: "用户反馈洞察流",
  workflowText: "从反馈中提炼洞察，形成可执行建议。",
  bottomNote: "报告预计完成：2 分钟"
} as const;

export const valueSection = {
  title: "一次岗位初筛，会帮你看清三件事"
} as const;

export const valueCards = [
  {
    title: "哪些任务正在贬值",
    text: "识别可被 AI 替代或弱化的工作环节，判断贬值风险与影响程度。",
    icon: "TrendingDown",
    tone: "danger"
  },
  {
    title: "哪些能力正在升值",
    text: "发现 AI 时代更具价值的能力，明确你的能力升级方向。",
    icon: "TrendingUp",
    tone: "growth"
  },
  {
    title: "第一个 AI 工作流动作",
    text: "获得一个能立刻上手的 AI 工作流，从今天开始改变工作方式。",
    icon: "Network",
    tone: "teal"
  }
] as const;

export const freeReportPreview = {
  sectionTitle: "免费报告预览",
  sectionSubtitle: "基于你输入的岗位，生成的岗位 AI 升值初筛报告节选。",
  title: "岗位 AI 升值初筛报告",
  profile: "本次输入：内容运营 · 互联网 · 1–3 年",
  verdict: "中风险",
  conclusion: "部分标准化环节正在被 AI 改变。",
  gaugeLabels: ["低风险：判断优势稳固", "中风险：部分环节被改变", "高风险：短期影响加剧"],
  conclusionTitle: "你的岗位 AI 升值初筛结论",
  conclusionSubtitle: "综合判断：中风险",
  summary:
    "从你现有的岗位工作环节来看，AI 不是在替代整个内容运营，而是在压缩其中标准化、可批量生成的部分。\n\n当前正在被压缩价值的是：重复内容初稿、热点信息整理和基础数据复盘。原因是：标题、脚本、笔记功能已经能被 AI 快速生成，单纯产出速度不再稀缺；曝光、点击、互动等基础数据整理也可以由工具提速，人工价值会被压缩。\n\n真正变贵的是：选题判断、用户理解和转化逻辑。你接下来更需要把重点从“完成产出”上移到“判断什么值得做、怎么做、如何推动结果”。",
  depreciatingAreas: [
    {
      title: "重复内容初稿",
      text: "标题、脚本、笔记初稿已经能被 AI 快速生成，单纯产出速度不再稀缺。"
    },
    {
      title: "基础内容数据复盘",
      text: "曝光、点击、互动等基础数据整理和摘要可由工具提速，人工价值会被压缩。"
    }
  ],
  riskRanking: [
    {
      title: "优先调整区",
      text: "这些工作正在被 AI 压低价值。",
      items: ["重复内容初稿", "基础内容数据复盘"],
      tone: "danger"
    },
    {
      title: "能力上移区",
      text: "这些工作不会消失，但要求你从执行转向判断。",
      items: ["热点信息整理", "选题策略和转化判断"],
      tone: "warning"
    },
    {
      title: "优势强化区",
      text: "这些仍然是你独有的价值，判断和协同难以替代。",
      items: ["深入用户理解", "价值主张输出"],
      tone: "growth"
    }
  ],
  signals: [
    "如果你主要停留在“发了多少内容”，短期内更容易被自动化压低价值。",
    "越依赖模板和热点搬运，越需要转向选题判断与转化闭环。"
  ],
  workflow: {
    title: "你的第一个 AI 上手动作：用户反馈洞察流",
    name: "用户反馈洞察流",
    description:
      "你当前最大的风险，不是 AI 会直接替你写内容，而是内容工作还停留在“临时灵感、临时写作、临时复盘”。",
    materials: ["最近一周的用户评论、私信或反馈", "3–5 条竞品内容评论区截图", "自己最近发布内容的数据表现"],
    advice: "先把这些材料复制到一个文档、表格，或直接分批交给 AI，不需要一次整理得很完整。",
    aiHelps: ["用户主要反馈点", "下一期选题建议", "用户反对意见"],
    humanJudgment: ["哪些问题和业务目标有关", "哪些选题值得继续做", "AI 是否遗漏用户语气和真实需求"],
    outputs: ["用户问题清单", "选题候选表", "内容复盘记录"],
    conclusion: "这会帮助你从“内容执行者”升级成“用户洞察型内容运营”。"
  }
} as const;

export const packagePlans = [
  {
    name: "免费岗位初筛",
    tag: "免费",
    items: ["识别岗位风险等级，低 / 中 / 高", "任务贬值与能力升值初步判断", "第一个 AI 上手动作建议", "报告预览精华版"],
    cta: "免费生成初筛报告",
    href: site.personalDiagnosisUrl
  },
  {
    name: "完整岗位 AI 升级包",
    tag: "推荐升级",
    items: [
      "3 个岗位专属 AI 上手动作，可直接使用",
      "材料清单，告诉你需要准备什么",
      "AI 指令链，可复制即用",
      "输出模板，包含文案、表格、结构化模板",
      "人工判断点，标注关键判断与避坑提醒",
      "岗位升值路径，形成能力升级路线图",
      "7 天启动计划，每日行动清单"
    ],
    cta: "预约获取完整升级包",
    href: "#upgrade-reservation"
  }
] as const;

export const packageModules = [
  { title: "AI 上手动作", text: "3 个专属动作，拿来就用。", icon: "ClipboardList" },
  { title: "材料清单", text: "需要准备什么，一目了然。", icon: "FilePenLine" },
  { title: "AI 指令链", text: "可复制的指令与执行流程。", icon: "Code2" },
  { title: "输出模板", text: "高质量产出结构化模板。", icon: "Pencil" },
  { title: "人工判断点", text: "关键判断与避坑提醒。", icon: "ShieldCheck" },
  { title: "岗位升值路径", text: "能力升级路线图。", icon: "Route" },
  { title: "7 天启动计划", text: "每日行动清单。", icon: "CalendarDays" }
] as const;

export const audienceCards = [
  { title: "内容运营", text: "想提升效率，更想做出更有影响力的内容。", icon: "Pencil" },
  { title: "产品经理", text: "想更懂用户，也想把需求变成更好的产品。", icon: "Box" },
  { title: "HR / 招聘", text: "想提高筛选效率，也想优化候选人体验。", icon: "Users" },
  { title: "数据分析", text: "想让数据真正驱动业务，而不只是做报表。", icon: "PieChart" }
] as const;

export const homeInsights = [
  {
    category: "趋势洞察",
    title: "AI 时代，岗位真正变化的不是名称，而是任务价值",
    summary: "岗位升级的起点不是工具清单，而是先看清哪些任务正在贬值，哪些判断能力正在升值。",
    date: "2024-05-20",
    image: "dashboard",
    href: "#article-task-value-change"
  }
] as const;

export const insights = [
  {
    id: "task-value-change",
    category: "趋势洞察",
    title: "AI 时代，岗位真正变化的不是名称，而是任务价值",
    summary: "岗位升级的起点不是工具清单，而是先看清哪些任务正在贬值，哪些判断能力正在升值。",
    date: "2024-06-12",
    reads: "6,842 阅读",
    readTime: "12 分钟阅读",
    author: "知汐 Labs 团队",
    image: "dashboard",
    href: "#article-task-value-change"
  }
] as const;

export const careerPage = {
  heroTitle: "岗位 AI 升值初筛，先看清你的任务价值变化",
  heroText:
    "免费、轻量的岗位价值诊断，识别贬值任务与升值能力，并为你生成第一个可执行的 AI 工作流。",
  badges: ["免费初筛", "2–3 分钟完成", "基于真实岗位任务模型", "保护你的隐私"],
  result: {
    title: "你的岗位 AI 升值初筛结果",
    tag: "免费版示例",
    verdict: "中风险",
    description: "部分标准化环节正在被 AI 改变。",
    metrics: [
      { label: "贬值任务", value: "2 项", note: "需及时优化" },
      { label: "升值能力", value: "3 项", note: "可重点建设" },
      { label: "上手动作", value: "3 步", note: "立即可执行" },
      { label: "建议优先级", value: "中高", note: "建议尽快启动" }
    ],
    footer: "完整版报告包含更深入的分析与可执行方案",
    link: "升级解锁完整报告"
  },
  inputs: ["岗位名称", "所在行业", "工作年限", "常做的主要工作"],
  outputs: ["中风险判断与变化信号", "贬值任务清单", "升值能力方向", "第一个 AI 工作流，3–5 步"],
  reasons: [
    "基于真实工作任务的 AI 影响分析",
    "识别被替代风险与价值提升机会",
    "给出具体动作，马上可执行",
    "为长期职业升级提供第一步抓手"
  ],
  process: [
    { title: "输入岗位信息", text: "填写岗位、行业、年限和你常做的主要工作。", icon: "ClipboardList" },
    { title: "拆解任务结构", text: "AI 拆解你的工作任务，识别关键任务与环节。", icon: "Network" },
    { title: "判断价值变化", text: "判断每个任务被 AI 影响的程度与价值变化趋势。", icon: "TrendingUp" },
    { title: "生成第一个 AI 工作流", text: "输出可立即执行的 3–5 步上手动作。", icon: "Route" }
  ],
  audienceTitle: "适合谁使用",
  audience: [
    { title: "内容运营", text: "担心内容创作、选题、分发被 AI 冲击，想找到升级方向。", icon: "Pencil" },
    { title: "产品经理", text: "想知道产品规划、需求分析等工作哪些会被替代，如何升级。", icon: "Box" },
    { title: "HR / 招聘", text: "担心简历筛选、初面等流程被 AI 替代，寻找更高价值的工作。", icon: "Users" },
    { title: "数据分析", text: "想了解数据清洗、报表分析等工作哪些环节最先被 AI 影响。", icon: "PieChart" }
  ],
  reportPreview: {
    freeTag: "本次将获得",
    paidTag: "更深入、更完整",
    footer: "完整版报告篇幅更长、分析更深入，助你制定系统的岗位升级计划。",
    link: "立即解锁完整报告"
  },
  faqs: [
    {
      question: "这个测试能预测我会被 AI 替代吗？",
      answer: "不能预测“是否被替代”，而是评估你当前任务的价值变化趋势，帮助你提前布局升级。"
    },
    {
      question: "需要提供公司或业务数据吗？",
      answer: "不需要。只需写个人岗位与工作描述，我们会保护你的隐私。"
    },
    {
      question: "会给具体的 AI 工具或工作流吗？",
      answer: "会。我们会基于你的工作场景，给出第一个可落地的 3-5 步 AI 工作流建议。"
    }
  ]
} as const;

export const samplePage = {
  heroTitle: "样例报告：先看免费初筛，再了解完整升级包",
  heroText:
    "不确定自己的岗位是否会被 AI 影响？先查看真实样例，再决定是否获取完整升级方案，清晰你的风险与机会。",
  benefits: [
    { title: "真实报告样式展示", text: "直观了解报告结构与深度。", icon: "ShieldCheck" },
    { title: "帮助你做出决策", text: "先体验，再决定是否升级。", icon: "Target" }
  ],
  freeTitle: "免费岗位初筛示例",
  freeSubtitle: "快速了解岗位 AI 风险等级与核心变化。",
  paidTitle: "完整岗位 AI 升级包示例",
  paidSubtitle: "从诊断到落地，提供系统性升级方案。",
  freeHighlights: ["中风险判断与趋势仪表盘", "贬值任务与替代风险清单，部分展示", "升值能力方向建议，部分展示", "第一个 AI 工作流，3–5 步"],
  paidHighlights: [
    "完整的任务价值变化分析",
    "详细的工作价值风险排序",
    "升值能力体系与建设路径",
    "短期变化信号追踪建议",
    "多个 AI 工作流方案与工具建议",
    "用户反馈与下一步行动计划"
  ],
  modules: [
    { title: "3 个岗位专属 AI 上手动作", text: "从不同场景切入，给你可执行的升级动作。", icon: "ClipboardList" },
    { title: "材料清单", text: "明确每个工作流需要准备哪些信息和素材。", icon: "FilePenLine" },
    { title: "AI 指令链", text: "提供可直接复用的提示词和执行步骤。", icon: "Code2" },
    { title: "输出模板", text: "让最终产出更稳定、更专业、更容易复用。", icon: "Pencil" },
    { title: "人工判断点", text: "标注哪些地方必须由你判断，避免被 AI 带偏。", icon: "ShieldCheck" },
    { title: "岗位升值路径", text: "从当前任务层级，推导下一步能力升级方向。", icon: "Route" },
    { title: "7 天启动计划", text: "把升级动作拆成一周内可以执行的小任务。", icon: "CalendarDays" }
  ],
  contentPreviews: {
    actions: [
      { title: "动作 1：用户反馈洞察流", text: "从用户评论和私信中提炼内容方向。" },
      { title: "动作 2：选题机会扫描仪", text: "从竞品和用户反馈中发现可测试选题。" },
      { title: "动作 3：内容价值提炼机", text: "把内容想法转化成更清晰的价值表达。" }
    ],
    materials: ["最近 7 天的用户评论、私信", "3–5 条竞品的客服或评价区截图", "近 3 篇高表现内容数据", "产品/服务介绍文档", "历史内容选题库"],
    prompt:
      "请梳理以下用户反馈，总结用户的核心关注点。将关注点按【高频 / 中频 / 低频】分类。针对每个高频关注点，给出 3 个可延展的选题方向。输出表格：关注点｜选题方向｜预期价值。",
    templateFields: ["选题方向", "目标人群", "价值点", "预期效果"],
    judgment: ["哪些选题更符合业务目标？", "哪些用户痛点最值得优先解决？", "哪些内容能带来更高的转化潜力？"],
    plan: ["Day 1–2：收集材料与输入 AI", "Day 3–4：产出选题与洞察", "Day 5–6：验证与优化方向", "Day 7：确定执行计划并启动"],
    path: ["执行操作层：内容生产与基础执行", "洞察分析层：用户洞察与内容策略", "策略影响层：内容策略与业务增长", "业务领导层：内容商业化与组织影响"],
    current: "执行操作层"
  },
  comparison: [
    ["核心定位", "快速判断 AI 风险等级，了解岗位受影响的核心环节。", "从诊断到落地，提供完整的升级方案与执行工具。"],
    ["报告深度", "基础诊断，聚焦风险识别与初步建议。", "深度诊断 + 落地方案，覆盖动作、工具与计划。"],
    ["提供内容", "风险等级、结论、贬值环节、风险排序、上手动作 1 个。", "3 个上手动作、材料清单、AI 指令链、输出模板、人工判断点、升值路径、7 天计划等。"],
    ["适合人群", "想快速了解风险，或初步判断是否需要升级的人。", "需要系统升级、落地执行，并追求长期竞争力的人。"]
  ],
  suggestion: "先用免费版快速判断风险，再升级完整版获取完整方案，有效提升你的工作价值。"
} as const;

export const aboutPage = {
  stats: [
    { value: "300+", label: "岗位价值分析样本", icon: "Network" }
  ],
  doing: [
    { title: "看清价值变化", text: "评估岗位任务在 AI 影响下的价值变化。", icon: "ShieldCheck" },
    { title: "识别 AI 介入点", text: "找出高价值、易交付的 AI 介入切入点。", icon: "ClipboardList" },
    { title: "形成可执行动作", text: "给出可直接落地的动作清单与建议", icon: "Route" }
  ],
  beliefs: [
    { title: "先看清任务价值变化，再设计工作流", text: "不盲目追工具，先判断任务的价值是否会改变。", icon: "Target" },
    { title: "不是焦虑测评，而是可执行建议", text: "给出清晰、具体、可执行的下一步行动。", icon: "Shield" },
    { title: "不是工具堆积，而是最小可行路径", text: "从小处切入，验证价值，再逐步扩展。", icon: "Network" },
    { title: "以人为本，让 AI 成为你的合作者", text: "保留人的判断与创造力，AI 放大你的影响力。", icon: "UserRound" }
  ],
  method: [
    { title: "真实任务", text: "梳理岗位的关键任务与真实工作场景", icon: "ClipboardList" },
    { title: "价值变化", text: "评估任务在 AI 影响下的价值变化趋势", icon: "LineChart" },
    { title: "AI 介入点", text: "识别高价值、可落地的 AI 介入环节", icon: "Target" },
    { title: "可执行动作", text: "生成具体的行动清单与工作流建议", icon: "ShieldCheck" }
  ],
  audience: [
    { title: "希望看清岗位未来价值的职场人", text: "想知道自己的岗位会如何被 AI 影响，下一步该学什么、做什么。" },
    { title: "希望提升效率与产出的专业人士", text: "希望用 AI 优化日常工作，减少重复劳动，专注高价值创造。" },
    { title: "希望培养 AI 时代竞争力的学习者", text: "希望系统理解 AI + 工作的方法，建立可迁移的能力与思维。" }
  ],
  futureDirections: ["更丰富的岗位覆盖", "更精准的价值评估", "更智能的行动建议"],
} as const;

export const contactDirections = [
  { value: "question", label: "常见问题" },
  { value: "feedback", label: "产品反馈" },
  { value: "business", label: "商务合作" },
  { value: "content", label: "内容转载" },
  { value: "support", label: "紧急支持" }
] as const;

export const contactTopics = [
  { title: "常见问题", text: "账户、报告、功能使用等常见问题咨询，我们会为你提供帮助。", icon: "MessageSquare" },
  { title: "产品反馈", text: "提出功能建议、体验问题或改进意见，帮助我们做得更好。", icon: "Pencil" },
  { title: "商务合作", text: "企业合作、定制服务、渠道合作或其他商务洽谈。", icon: "Briefcase" },
  { title: "内容转载", text: "报告内容引用、转载授权或媒体沟通相关事宜。", icon: "FilePenLine" },
  { title: "紧急支持", text: "如遇账号异常或报告访问问题，请在留言中选择“常见问题”并详细描述，我们将优先处理。", icon: "Info" }
] as const;

export const article = {
  id: "task-value-change",
  category: "趋势洞察",
  title: "AI 时代，岗位真正变化的不是名称，而是任务价值",
  date: "2026-06-24",
  readTime: "6 分钟阅读",
  author: "知汐 Labs · 岗位价值观察",
  intro:
    "AI 带来的岗位变化，不只是某个岗位名称会不会消失，而是岗位内部任务价值的重新排序。第一阶段更值得判断的是：哪些任务被 AI 压低价值，哪些判断能力正在升值，以及下一步该先升级哪个工作流。",
  quote: "岗位升级的起点不是工具清单，而是看清任务价值正在怎样变化。",
  sections: [
    {
      index: "01",
      title: "岗位不是一个整体，任务才是变化单位",
      text:
        "同一个岗位里，既有资料整理、文本生成、格式处理这样的高重复任务，也有选题判断、客户理解、结果复盘这样的高判断任务。AI 进入工作后，变化首先发生在这些具体任务上。"
    },
    {
      index: "02",
      title: "工具越多，越需要先判断升级顺序",
      text:
        "很多人已经开始用 AI，但仍然停留在零散提问和临时复制。真正能带来岗位升值的，不是继续堆工具，而是找到最值得先升级的 1 到 3 个真实任务。"
    },
    {
      index: "03",
      title: "可执行工作流比泛泛建议更重要",
      text:
        "一个好的初筛结果，应该告诉你可以先做什么：输入什么、让 AI 处理哪一步、输出如何检查、哪些判断仍然由人负责。这样才可能从报告变成行动。"
    },
    {
      index: "04",
      title: "先小步验证，再沉淀个人工作流",
      text:
        "岗位升级不是一次性结论，而是持续迭代的过程。先跑一个低风险、高频、可检查的工作流，再根据结果扩展到更多任务。"
    }
  ]
} as const;

export const footerGroups = [
  { title: "产品与服务", links: [{ label: "岗位升值初筛", href: "#career-upgrade" }, { label: "样例报告", href: "#sample-report" }, { label: "岗位观察", href: "#insights" }] },
  { title: "关于我们", links: [{ label: "关于知汐", href: "#about" }, { label: "联系我们", href: "#contact" }] },
  { title: "帮助与支持", links: [{ label: "联系", href: "#contact" }, { label: "隐私政策", href: "#privacy" }, { label: "用户协议", href: "#terms" }] }
] as const;
